import React from 'react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  ClipboardDocumentListIcon,
  PencilSquareIcon,
} from '@heroicons/react/20/solid';

import AudioRecorder from '../../utilities/AudioRecorder';
import Transcribe from './Transcribe';

const EndPoint = process.env.REACT_APP_API_ENDPOINT;

// utilities
const formatDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
};


const IconButton = ({ label, title, onClick, children, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative group rounded-xl px-3 py-2 text-white bg-sky-800 hover:bg-sky-900 focus:outline-none focus:ring-2 focus:ring-sky-400 ${className}`}
    aria-label={label}
    title={title || ""}
  >
    {children}
    <span
      role="tooltip"
      className="
        pointer-events-none
        absolute -top-10 left-1/2 -translate-x-1/2
        whitespace-nowrap rounded bg-slate-700 p-2 text-sm text-white shadow
        opacity-0 translate-y-1
        transition-opacity transition-transform duration-150
        group-hover:opacity-100 group-hover:translate-y-0
        group-focus-visible:opacity-100 group-focus-visible:translate-y-0
      "
    >
      {label}
    </span>
  </button>
);

class MentalHealth extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      // edit flags
      isEditingMHCP: false,
      isEditingFormulation: false,
      isEditingSafety: false,
      isEditingRelapse: false,
      isHelper: false,

      // smart paste
      smartPasteOpen: false,
      smartPasteText: "",
      smartPasteError: "",
      smartPasteBusy: false,
      creditsBalance: null,
      // tab view
      activeTab: "Form"
    };
  }

  toggleEdit = (flagName) => {
    this.setState((prev) => {
      const wasEditing = prev[flagName];
      const nextVal = !wasEditing;

      // if we are turning editing OFF, save to localStorage
      if (wasEditing) {
          this.props.onSaveDraft();
      }

      return { [flagName]: nextVal };
    });
  };

  // --- change tab ---
  handleTabChange = (tab) => {
    this.setState({ activeTab: tab });
  };

  handleSectionChange = (section, key, value) => {
    const current = this.props.value || {};
    const sectionObj = current[section] || {};

    const nextValue = {
      ...current,
      [section]: {
        ...sectionObj,
        [key]: value,
      },
    };

    if (this.props.onChange) {
      this.props.onChange(nextValue);
    }
  };


  // Placeholder so the button doesn't blow up
  openConsultationNote = () => {
    toast.info('Consultation note not implemented yet');
  };

  // ---- Smart Paste / Audio ----
  openSmartPaste = () => {
    this.setState({ smartPasteOpen: true, smartPasteText: "", smartPasteError: "" });
  };

  closeSmartPaste = () => {
    if (this.state.smartPasteBusy) return;
    this.setState({ smartPasteOpen: false, smartPasteText: "", smartPasteError: "" });
  };

  handleTranscription = (data) => {
    const transcription = data.transcription;
    const creditUsed = data.output.amount;
    const newBalance = data.output.newBalance;
    const reason = data.output.reason;

    toast.info(`${creditUsed} credits was used to ${reason}`, { autoClose: 1500 });
    this.setState(
      { creditsBalance: newBalance, smartPasteText: transcription },
      () => {
        this.toastId = toast.loading("Filling up form...");
        this.handleTextProcessing(this.toastId);
      }
    );
  };

  handleTextProcessing = async (toastId) => {
    const { smartPasteText } = this.state;
    if (!smartPasteText.trim()) {
      this.setState({ smartPasteError: "Please paste or type something first." });
      return;
    }

    this.setState({ smartPasteBusy: true, smartPasteError: "" });


    const current = this.props.value; 

    try {
      const res = await axios.post(
        `${EndPoint}/careplan/mhcp`,
        {
          transcription: smartPasteText,
          initData: current
        },
        { withCredentials: true }
      );

      const payload = res?.data;
      const ai = JSON.parse(payload?.respond);

      //mental state exam
      const mse =
        ai && typeof ai.mentalStateExamination === "object"
        ? Object.entries(ai.mentalStateExamination)
            .map(([k, v]) => `${k}: ${v}`)
            .join("\n ")
        : ai?.mentalStateExamination || "";

      if (ai) {
        const current = this.props.value || {};

        const nextValue = {
          ...current,
          mhcp: {
            ...(current.mhcp || {}),
            presentingIssue: ai.presentingIssue,
            history: ai.history,
            pastHistory: ai.pastHistory,
            childhoodHistory: ai.childhoodHistory,
            familyHistory: ai.familyHistory,
            parentChildNeed: ai.parentChildNeed,
            occupation: ai.occupation,
            social: ai.social,
            substance: ai.substance,
            currentMedication: ai.currentMedication,
            allergies: ai.allergies,
            treatmentHistory: ai.treatmentHistory,
            medicationHistory: ai.medicationHistory,
            relevantExamination: ai.relevantExamination,
            relevantInvestigation: ai.relevantInvestigation,
            devTesting: ai.devTesting,
            mentalStateExamination: mse,
            risk: ai.risk,
            tools: ai.tools,
            diagnosis: ai.diagnosis,
          },

          mhformulation: {
            ...(current.mhformulation || {}),
            predisposing: ai.predisposing,
            precipitating: ai.precipitating,
            perpetuating: ai.perpetuating,
            protective: ai.protective,
          },

          mhsafety: {
            ...(current.mhsafety || {}),
            reason: ai.reason,
            cando: ai.cando,
            help: ai.help,
            call: ai.call,
            talk: ai.talk,
            professional: ai.professional,
          },

          relapsePrevention: {
            ...(current.relapsePrevention || {}),
            triggers: ai.triggers,
            warningSigns: ai.warningSigns,
            copingStrategy: ai.copingStrategy,
            support: ai.support,
            emergency: ai.emergency,
          },
        };

        if (this.props.onChange) {
          this.props.onChange(nextValue);
        }
      }

      // credits part unchanged
      if (payload?.output) {
        const creditUsed = payload.output.amount;
        const newBalance = payload.output.newBalance;
        const reason = payload.output.reason;
        toast.info(`${creditUsed} credits was used to ${reason}`, { autoClose: 1500 });
        this.props.onCredits(newBalance);
      }

      this.setState({
        smartPasteBusy: false,
        smartPasteOpen: false,
        smartPasteText: "",
      });

      toast.update(toastId, {
        render: "Finish Thinking",
        type: "success",
        isLoading: false,
        autoClose: 1100,
      });
    } catch (err) {
      console.error(err);
      this.setState({
        smartPasteBusy: false,
        smartPasteError:
          err?.response?.data?.error || err?.message || "Request failed",
      });
    }
  };

  // getMHCPWordHTML = () => {
  //   const s = this.state;
  //     const {
  //   mhcp = {},
  //   mhformulation = {},
  //   mhsafety = {},
  //   relapsePrevention = {},
  // } = this.props.value || {};

  //   const esc = (x) =>
  //     String(x ?? "")
  //       .replace(/&/g, "&amp;")
  //       .replace(/</g, "&lt;")
  //       .replace(/>/g, "&gt;");

  //   const br = (x) =>
  //     esc(x)
  //       .replace(/\r?\n/g, "<br/>");

  //   const toLines = (v) => {
  //     if (Array.isArray(v)) {
  //       return v
  //         .filter(Boolean)
  //         .map((x) => (typeof x === "string" ? x : x?.title || ""))
  //         .filter(Boolean);
  //     }
  //     return String(v || "")
  //       .split(/\r?\n/)
  //       .map((t) => t.trim())
  //       .filter(Boolean);
  //   };

  //   const linesHTML = (v) => {
  //     const arr = toLines(v);
  //     return arr.length
  //       ? arr
  //           .map(
  //             (ln) =>
  //               `<div style="margin:0 0 4pt 0;"><span style="font-size:8pt; mso-ansi-font-size:8.0pt; mso-bidi-font-size:8.0pt;">${esc(
  //                 ln
  //               )}</span></div>`
  //           )
  //           .join("")
  //       : "";
  //   };

  //   // Styles (same flavour as your GPCCMP generator)
  //   const base =
  //     'font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif; font-size:8pt; line-height:1.3; color:#111; ' +
  //     'mso-ansi-font-size:8.0pt; mso-bidi-font-size:8.0pt;';
  //   const tbl =
  //     "border-collapse:collapse; mso-table-lspace:0pt; mso-table-rspace:0pt; " +
  //     "table-layout:fixed; width:6.8in; max-width:100%; margin:6pt 0 10pt;";
  //   const th =
  //     "border:1px solid #ccc; padding:6pt; vertical-align:top; " +
  //     "font-size:8pt; mso-ansi-font-size:8.0pt; mso-bidi-font-size:8.0pt; font-weight:600; background:#f3f4f6; " +
  //     "word-wrap:break-word; overflow-wrap:anywhere; word-break:break-word;";
  //   const td =
  //     "border:1px solid #ccc; padding:6pt; vertical-align:top; " +
  //     "font-size:8pt; mso-ansi-font-size:8.0pt; mso-bidi-font-size:8.0pt; " +
  //     "word-wrap:break-word; overflow-wrap:anywhere; word-break:break-word;";
  //   const mutedLabel = 'font-size:8pt; color:#555; mso-ansi-font-size:8.0pt; mso-bidi-font-size:8.0pt;';

  //   const h2Style = {
  //     fontSize: "9pt",
  //     fontWeight: 700,
  //     margin: "6pt 0",
  //     padding: 0,
  //   };

  //   const h3Style = {
  //     fontSize: "8pt",
  //     fontWeight: 600,
  //     margin: "6pt 0 2pt",
  //     padding: 0,
  //   };

  //   const bodyTextStyle = {
  //     fontSize: "8pt",
  //     lineHeight: "1.3",
  //   };

  //   const textSpan = (t) =>
  //     `<span style="font-size:8pt; mso-ansi-font-size:8.0pt; mso-bidi-font-size:8.0pt;">${esc(
  //       t
  //     )}</span>`;

  //   const addr = [s.ptStreet, s.ptCity, s.ptPostcode, s.ptState]
  //     .filter(Boolean)
  //     .join(" ");

  //   return `
  // <!doctype html>
  // <html>
  // <head><meta charset="utf-8"></head>
  // <body>
  //   <font size="1">
  //     <div style="${base}">
      

  //       <!-- PATIENT ASSESSMENT -->
  //       <h2>Patient Assessment – Mental Health</h2>

  //       <h3 style = {h3Style} >Reasons for presenting</h3>
  //       <div>${br(mhcp.presentingIssue || "")}</div>

  //       <h3 style = {h3Style} >History of current episode</h3>
  //       <div>${br(mhcp.history || "")}</div>

  //       <h3 style = {h3Style} >Patient history</h3>
  //       <div>
  //         ${
  //           mhcp.pastHistory
  //             ? `<div><b>Mental / medical / social:</b> ${br(mhcp.pastHistory)}</div>`
  //             : ""
  //         }
  //         ${
  //           mhcp.childhoodHistory
  //             ? `<div><b>Childhood history:</b> ${br(mhcp.childhoodHistory)}</div>`
  //             : ""
  //         }
  //         ${
  //           mhcp.occupation
  //             ? `<div><b>Occupation:</b> ${br(mhcp.occupation)}</div>`
  //             : ""
  //         }
  //         ${
  //           mhcp.social
  //             ? `<div><b>Salient social history:</b> ${br(mhcp.social)}</div>`
  //             : ""
  //         }
  //         ${
  //           mhcp.devTesting
  //             ? `<div><b>Developmental issues / testing:</b> ${br(mhcp.devTesting)}</div>`
  //             : ""
  //         }
  //       </div>

  //       <h3 style = {h3Style} >Family history of mental illness</h3>
  //       <div>${br(mhcp.familyHistory || "")}</div>

  //       <h3 style = {h3Style} >Parent and children needs</h3>
  //       <div>${br(mhcp.parentChildNeed || "")}</div>

  //       <h3 style = {h3Style} >Current domestic and social circumstances</h3>
  //       <div>${br(mhcp.social || "")}</div>

  //       <h3 style = {h3Style} >Salient substance use issues</h3>
  //       <div>${br(mhcp.substance || "")}</div>

  //       <h3 style = {h3Style} >Current medications</h3>
  //       <div>${br(mhcp.currentMedication || "")}</div>

  //       <h3 style = {h3Style} >History of medication and other treatments</h3>
  //       <div>${br(mhcp.treatmentHistory || mhcp.medicationHistory || "")}</div>

  //       <h3 style = {h3Style} >Allergies</h3>
  //       <div>${br(mhcp.allergies || "")}</div>

  //       <h3 style = {h3Style} >Relevant physical examination and investigations</h3>
  //       <div>
  //         ${
  //           mhcp.relevantExamination
  //             ? `<div><b>Examination:</b> ${br(mhcp.relevantExamination)}</div>`
  //             : ""
  //         }
  //         ${
  //           mhcp.relevantInvestigation
  //             ? `<div><b>Investigations:</b> ${br(mhcp.relevantInvestigation)}</div>`
  //             : ""
  //         }
  //       </div>

  //       <h3 style = {h3Style} >Results of previous psychological / developmental testing</h3>
  //       <div>${br(mhcp.devTesting || "")}</div>

  //       <!-- MENTAL STATE EXAMINATION -->
  //       <h2 style = {h2Style} >Comments on Current Mental State Examination</h2>
    
  //       <div style="margin-top:4pt;">
  //         ${
  //           linesHTML(mhcp.mentalStateExamination && mhcp.mentalStateExamination.length
  //             ? mhcp.mentalStateExamination
  //             : "")
  //         || "<span style=\"font-size:8pt;\">No MSE comments recorded.</span>"
  //         }
  //       </div>

  //       <!-- RISK -->
  //       <h2 style = {h2Style} >Risk Assessment</h2>
  
  //       <div style="margin-top:4pt;">
  //         ${br(mhcp.risk || "No specific risks documented.")}
  //       </div>

  //       <!-- ASSESSMENT TOOL & DIAGNOSIS -->
  //       <h2 style = {h2Style} >Assessment Tools & Provisional Diagnosis</h2>
  //       <table class="noborder" style="width:100%; margin-bottom:6pt;">
  //         <tr>
  //           <td style="vertical-align:top; width:50%;">
  //             <b>Assessment / outcome tool used</b><br/>
  //             ${br(mhcp.tools || "")}
  //           </td>
  //           <td style="vertical-align:top; width:50%;">
  //             <b>Provisional diagnosis of mental health disorder</b><br/>
  //             ${br(mhcp.diagnosis || "")}
  //           </td>
  //         </tr>
  //       </table>

  //       <!-- CASE FORMULATION -->
  //       <h2 style = {h2Style} >Case Formulation</h2>
  //       <table border="0" cellspacing="0" cellpadding="0" style="${tbl}">
  //         <tr>
  //           <th style="${th}">${textSpan("Predisposing factors")}</th>
  //           <th style="${th}">${textSpan("Precipitating factors")}</th>
  //         </tr>
  //         <tr>
  //           <td style="${td}">${br(mhformulation.predisposing || "")}</td>
  //           <td style="${td}">${br(mhformulation.precipitating || "")}</td>
  //         </tr>
  //         <tr>
  //           <th style="${th}">${textSpan("Perpetuating factors")}</th>
  //           <th style="${th}">${textSpan("Protective factors")}</th>
  //         </tr>
  //         <tr>
  //           <td style="${td}">${br(mhformulation.perpetuating || "")}</td>
  //           <td style="${td}">${br(mhformulation.protective || "")}</td>
  //         </tr>
  //       </table>

  //       <!-- SAFETY & RELAPSE PLAN -->
  //       <h2 style = {h2Style} >Safety Plan & Intervention / Relapse Prevention</h2>
  //       <table border="0" cellspacing="0" cellpadding="0" style="${tbl}">
  //         <tr>
  //           <th style="${th}">${textSpan("Reasons to live / stay safe")}</th>
  //           <th style="${th}">${textSpan("What I can do myself")}</th>
  //         </tr>
  //         <tr>
  //           <td style="${td}">${br(mhsafety.reason || "")}</td>
  //           <td style="${td}">${br(mhsafety.cando || "")}</td>
  //         </tr>

  //         <tr>
  //           <th style="${th}">${textSpan("Who can help me / who I can call")}</th>
  //           <th style="${th}">${textSpan("What I can talk about / professional contacts")}</th>
  //         </tr>
  //         <tr>
  //           <td style="${td}">
  //             ${
  //               mhsafety.help
  //                 ? `<div><b>Supports:</b> ${br(mhsafety.help)}</div>`
  //                 : ""
  //             }
  //             ${
  //               mhsafety.call
  //                 ? `<div><b>Phone contacts:</b> ${br(mhsafety.call)}</div>`
  //                 : ""
  //             }
  //           </td>
  //           <td style="${td}">
  //             ${
  //               mhsafety.talk
  //                 ? `<div><b>Topics to discuss:</b> ${br(mhsafety.talk)}</div>`
  //                 : ""
  //             }
  //             ${
  //               mhsafety.professional
  //                 ? `<div><b>Professionals / services:</b> ${br(mhsafety.professional)}</div>`
  //                 : ""
  //             }
  //           </td>
  //         </tr>

  //         <tr>
  //           <th style="${th}">${textSpan("Triggers & warning signs")}</th>
  //           <th style="${th}">${textSpan("Coping strategies & supports")}</th>
  //         </tr>
  //         <tr>
  //           <td style="${td}">
  //             ${
  //               relapsePrevention.triggers
  //                 ? `<div><b>Triggers:</b> ${br(relapsePrevention.triggers)}</div>`
  //                 : ""
  //             }
  //             ${
  //               relapsePrevention.warningSigns
  //                 ? `<div><b>Warning signs:</b> ${br(relapsePrevention.warningSigns)}</div>`
  //                 : ""
  //             }
  //           </td>
  //           <td style="${td}">
  //             ${
  //               relapsePrevention.copingStrategy
  //                 ? `<div><b>Coping strategies:</b> ${br(relapsePrevention.copingStrategy)}</div>`
  //                 : ""
  //             }
  //             ${
  //               relapsePrevention.support
  //                 ? `<div><b>Supports / services:</b> ${br(relapsePrevention.support)}</div>`
  //                 : ""
  //             }
  //             ${
  //               relapsePrevention.emergency
  //                 ? `<div><b>Emergency / crisis arrangements:</b> ${br(relapsePrevention.emergency)}</div>`
  //                 : ""
  //             }
  //           </td>
  //         </tr>
  //       </table>

  //       <!-- DATE & SIGNATURE -->
  //       <h2 style = {h2Style} >Completion</h2>
  //       <p><b>Date plan completed:</b> ${esc(
  //         typeof formatDate === "function" ? formatDate(s.todaysDate) : s.todaysDate || ""
  //       )}</p>

  //       <p><b>GP Signature:</b></p>
  //       ${
  //         s.gpSignatureDataUrl
  //           ? `<img src="${esc(
  //               s.gpSignatureDataUrl
  //             )}" alt="Signature" style="display:block;width:80pt;height:auto;margin:0;border:0;" />`
  //           : "_______________________________"
  //       }

  //       <p><b>GP Name:</b> ${esc(s.gpName || "")}</p>
  //     </div>
  //   </font>
  // </body>
  // </html>`;
  // };

  getMHCPWordHTML = () => {
    const s = this.state;
    const {
      mhcp = {},
      mhformulation = {},
      mhsafety = {},
      relapsePrevention = {},
    } = this.props.value || {};

    const esc = (x) =>
      String(x ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

    const br = (x) => esc(x).replace(/\r?\n/g, "<br/>");

    const toLines = (v) => {
      if (Array.isArray(v)) {
        return v
          .filter(Boolean)
          .map((x) => (typeof x === "string" ? x : x?.title || ""))
          .filter(Boolean);
      }
      return String(v || "")
        .split(/\r?\n/)
        .map((t) => t.trim())
        .filter(Boolean);
    };

    const linesHTML = (v, fallback = "") => {
      const arr = toLines(v);
      if (!arr.length) {
        return fallback
          ? `<div>${br(fallback)}</div>`
          : "";
      }
      return arr
        .map(
          (ln) =>
            `<div style="margin:0 0 4pt 0;">${br(ln)}</div>`
        )
        .join("");
    };

  const base =
    "font-family:Arial, Helvetica, sans-serif; font-size:8pt; line-height:1.35; color:#111; " +
    "mso-ansi-font-size:8.0pt; mso-bidi-font-size:8.0pt;";

  const sectionHeaderStyle =
    "margin:14pt 0 0 0; padding:9pt 10pt; background:#075985; color:#fff; font-weight:700; " +
    "font-size:8pt; line-height:1.2; mso-ansi-font-size:8.0pt; mso-bidi-font-size:8.0pt; " +
    "border:0;";

  const tableStyle =
    "border-collapse:collapse; table-layout:fixed; width:100%; margin:0 0 16pt 0; " +
    "mso-table-lspace:0pt; mso-table-rspace:0pt;";
    const labelStyle =
      "border:1px solid #d1d5db; padding:6pt; vertical-align:top; font-weight:600; background:#f9fafb; " +
      "font-size:8pt; width:25%; word-wrap:break-word; overflow-wrap:anywhere; word-break:break-word;";

    const valueStyle =
      "border:1px solid #d1d5db; padding:6pt; vertical-align:top; background:#fff; " +
      "font-size:8pt; width:25%; word-wrap:break-word; overflow-wrap:anywhere; word-break:break-word;";

  const label2ColStyle =
    "border-top:1px solid #d1d5db; border-bottom:1px solid #d1d5db; border-left:0; border-right:0; " +
    "padding:8pt 10pt; vertical-align:top; font-weight:600; background:#f9fafb; " +
    "font-size:8pt; width:240px; word-wrap:break-word; overflow-wrap:anywhere; word-break:break-word;";

  const value2ColStyle =
    "border-top:1px solid #d1d5db; border-bottom:1px solid #d1d5db; border-left:0; border-right:0; " +
    "padding:8pt 10pt; vertical-align:top; background:#fff; " +
    "font-size:8pt; word-wrap:break-word; overflow-wrap:anywhere; word-break:break-word;";

    const fourColLabelLeftStyle =
    "border-top:1px solid #d1d5db; border-bottom:1px solid #d1d5db; border-left:0; border-right:0; " +
    "padding:8pt 10pt; vertical-align:top; font-weight:600; background:#f9fafb; " +
    "font-size:8pt; width:22%; word-wrap:break-word; overflow-wrap:anywhere; word-break:break-word;";

  const fourColValueLeftStyle =
    "border-top:1px solid #d1d5db; border-bottom:1px solid #d1d5db; border-left:0; border-right:1px solid #d1d5db; " +
    "padding:8pt 10pt; vertical-align:top; background:#fff; " +
    "font-size:8pt; width:28%; word-wrap:break-word; overflow-wrap:anywhere; word-break:break-word;";

  const fourColLabelRightStyle =
    "border-top:1px solid #d1d5db; border-bottom:1px solid #d1d5db; border-left:0; border-right:0; " +
    "padding:8pt 10pt; vertical-align:top; font-weight:600; background:#f9fafb; " +
    "font-size:8pt; width:22%; word-wrap:break-word; overflow-wrap:anywhere; word-break:break-word;";

  const fourColValueRightStyle =
    "border-top:1px solid #d1d5db; border-bottom:1px solid #d1d5db; border-left:0; border-right:0; " +
    "padding:8pt 10pt; vertical-align:top; background:#fff; " +
    "font-size:8pt; width:28%; word-wrap:break-word; overflow-wrap:anywhere; word-break:break-word;";

    const noteStyle =
      "font-size:6pt; color:#fff; margin:2pt 0 8pt 2pt; padding:2pt 0 8pt 2pt; background:#075985;";

    const sectionHeader = (title) =>
      `<div style="${sectionHeaderStyle}">${esc(title)}</div>`;

    const twoColRow = (label, value, fallback = "") => `
      <tr>
        <td style="${label2ColStyle}">${esc(label)}</td>
        <td style="${value2ColStyle}">${value && String(value).trim() ? value : br(fallback)}</td>
      </tr>
    `;

  const fourColRow = (
    label1,
    value1,
    label2,
    value2,
    options = {}
  ) => {
    const { colSpanSecondValue = false } = options;

    if (colSpanSecondValue) {
      return `
        <tr>
          <td style="${fourColLabelLeftStyle}">${esc(label1 || "")}</td>
          <td style="${fourColValueLeftStyle}">${value1 || ""}</td>
          <td style="${fourColLabelRightStyle}"></td>
          <td style="${fourColValueRightStyle}"></td>
        </tr>
      `;
    }

    return `
      <tr>
        <td style="${fourColLabelLeftStyle}">${esc(label1 || "")}</td>
        <td style="${fourColValueLeftStyle}">${value1 || ""}</td>
        <td style="${fourColLabelRightStyle}">${esc(label2 || "")}</td>
        <td style="${fourColValueRightStyle}">${value2 || ""}</td>
      </tr>
    `;
  };

    const patientAddress = [s.ptStreet, s.ptCity, s.ptPostcode, s.ptState]
      .filter(Boolean)
      .join(" ");

    const patientHistoryRows = [
      mhcp.pastHistory ? `Mental / medical / social: ${mhcp.pastHistory}` : null,
      mhcp.childhoodHistory ? `Childhood history: ${mhcp.childhoodHistory}` : null,
      mhcp.occupation ? `Occupation: ${mhcp.occupation}` : null,
      mhcp.social ? `Salient social history: ${mhcp.social}` : null,
      mhcp.devTesting
        ? `Developmental issues / testing: ${mhcp.devTesting}`
        : null,
    ].filter(Boolean);

    const relevantExamInvestigation = [
      mhcp.relevantExamination
        ? `Examination: ${mhcp.relevantExamination}`
        : null,
      mhcp.relevantInvestigation
        ? `Investigations: ${mhcp.relevantInvestigation}`
        : null,
    ].filter(Boolean);

    const mseRows =
      typeof mhcp.mentalStateExamination === "object" &&
      mhcp.mentalStateExamination !== null &&
      !Array.isArray(mhcp.mentalStateExamination)
        ? Object.entries(mhcp.mentalStateExamination)
        : [];

    return `
  <!doctype html>
  <html>
  <head>
    <meta charset="utf-8" />
  </head>
  <body>
    <div style="${base}">
      ${
        s.practiceLetterhead
          ? `<div style="font-size:16pt; font-weight:700; text-align:center; white-space:pre-wrap; margin-bottom:6pt;">${br(
              s.practiceLetterhead
            )}</div>`
          : ""
      }

      <div style="text-align:center; font-size:11pt; font-weight:700; margin:2pt 0;">
        GP MENTAL HEALTH TREATMENT PLAN – ADULT
      </div>
      <div style="text-align:center; font-size:8pt; color:#555; margin:0 0 8pt 0;">
        MBS Items 2715/2717
      </div>

      ${sectionHeader("Contact and Demographic Details")}
      <table border="0" cellspacing="0" cellpadding="0" style="${tableStyle}">
        ${fourColRow("GP name", br(s.gpName || ""), "GP phone", br(s.gpPhone || ""))}
        ${fourColRow(
          "Practice name",
          br(s.practiceName || ""),
          "Provider number",
          br(s.providerNumber || "")
        )}
        ${fourColRow(
          "Practice address",
          br(s.practiceAddress || ""),
          "",
          "",
          { colSpanSecondValue: true }
        )}
        ${fourColRow(
          "Patient name",
          br(s.ptFullName || ""),
          "Date of birth",
          br(typeof formatDate === "function" ? formatDate(s.ptDoB) : s.ptDoB || "")
        )}
        ${fourColRow(
          "Patient address",
          br(patientAddress),
          "Phone",
          br(s.ptPhoneMob || s.ptPhoneH || "")
        )}
      </table>

      ${sectionHeader("Patient Assessment – Mental Health")}
      <table border="0" cellspacing="0" cellpadding="0" style="${tableStyle}">
        ${twoColRow("Reasons for presenting", br(mhcp.presentingIssue || ""))}
        ${twoColRow("History of current episode", br(mhcp.history || ""))}
        ${twoColRow("Patient history", linesHTML(patientHistoryRows))}
        ${twoColRow("Family history of mental illness", br(mhcp.familyHistory || ""))}
        ${twoColRow("Parent and children needs", br(mhcp.parentChildNeed || ""))}
        ${twoColRow(
          "Current domestic and social circumstances",
          br(mhcp.social || "")
        )}
        ${twoColRow("Salient substance use issues", br(mhcp.substance || ""))}
        ${twoColRow("Current medications", br(mhcp.currentMedication || ""))}
        ${twoColRow(
          "History of medication and other treatments",
          br(mhcp.treatmentHistory || mhcp.medicationHistory || "")
        )}
        ${twoColRow("Allergies", br(mhcp.allergies || ""))}
        ${twoColRow(
          "Relevant physical examination and investigations",
          linesHTML(relevantExamInvestigation)
        )}
        ${twoColRow(
          "Results of previous psychological / developmental testing",
          br(mhcp.devTesting || "")
        )}
      </table>

      ${sectionHeader("Comments on Current Mental State Examination")}
          <div style="${noteStyle}">
        Appearance, cognition, thought process, thought content, attention, memory, insight, behaviour, speech, mood and affect, perception, judgement, orientation.
      </div>
      <table border="0" cellspacing="0" cellpadding="0" style="${tableStyle}">
        ${
          mseRows.length > 0
            ? mseRows
                .map(([key, value]) => twoColRow(key, br(value || "")))
                .join("")
            : twoColRow(
                "Comments",
                linesHTML(mhcp.mentalStateExamination, "No MSE comments recorded.")
              )
        }
      </table>
  

      ${sectionHeader("Risk Assessment")}
      <div style="${noteStyle}">
        Suicide, self-harm, harm to others – ideation, intent, plan; document actions taken if high risk.
      </div>
      <table border="0" cellspacing="0" cellpadding="0" style="${tableStyle}">
        ${twoColRow(
          "Risk details",
          br(mhcp.risk || "No specific risks documented.")
        )}
      </table>
      

      ${sectionHeader("Assessment and Diagnosis")}
      <table border="0" cellspacing="0" cellpadding="0" style="${tableStyle}">
        ${twoColRow("Assessment / outcome tool used", br(mhcp.tools || ""))}
        ${twoColRow(
          "Provisional diagnosis of mental health disorder",
          br(mhcp.diagnosis || "")
        )}
      </table>

      ${sectionHeader("Case Formulation")}
      <table border="0" cellspacing="0" cellpadding="0" style="${tableStyle}">
        ${twoColRow("Predisposing factors", br(mhformulation.predisposing || ""))}
        ${twoColRow(
          "Precipitating factors",
          br(mhformulation.precipitating || "")
        )}
        ${twoColRow(
          "Perpetuating factors",
          br(mhformulation.perpetuating || "")
        )}
        ${twoColRow("Protective factors", br(mhformulation.protective || ""))}
      </table>

      ${sectionHeader("Intervention / Relapse Prevention Plan & Safety Plan")}
      <table border="0" cellspacing="0" cellpadding="0" style="${tableStyle}">
        ${fourColRow(
          "Reasons to live / stay safe",
          br(mhsafety.reason || ""),
          "What I can do myself",
          br(mhsafety.cando || "")
        )}
        ${fourColRow(
          "Who can help me",
          br(mhsafety.help || ""),
          "Who I can call",
          br(mhsafety.call || "")
        )}
        ${fourColRow(
          "What I can talk about",
          br(mhsafety.talk || ""),
          "Professional contacts",
          br(mhsafety.professional || "")
        )}
        ${fourColRow(
          "Triggers",
          br(relapsePrevention.triggers || ""),
          "Warning signs",
          br(relapsePrevention.warningSigns || "")
        )}
        ${fourColRow(
          "Coping strategies",
          br(relapsePrevention.copingStrategy || ""),
          "Supports / services",
          br(relapsePrevention.support || "")
        )}
        ${fourColRow(
          "Emergency / crisis arrangements",
          br(relapsePrevention.emergency || ""),
          "",
          "",
          { colSpanSecondValue: true }
        )}
      </table>

      ${sectionHeader("Plan Completion")}
      <table border="0" cellspacing="0" cellpadding="0" style="${tableStyle}">
        ${twoColRow(
          "Date plan completed",
          br(typeof formatDate === "function" ? formatDate(s.todaysDate) : s.todaysDate || "")
        )}
        <tr>
          <td style="${label2ColStyle}">GP signature</td>
          <td style="${value2ColStyle}">
            ${
              s.gpSignatureDataUrl
                ? `<img src="${esc(
                    s.gpSignatureDataUrl
                  )}" alt="Signature" style="display:block; width:80pt; height:auto; margin:0; border:0;" />`
                : ""
            }
          </td>
        </tr>
        ${twoColRow("GP Name", br(s.gpName || ""))}
      </table>
    </div>
  </body>
  </html>`;
  };

  copyMHCPToClipboard = async () => {
    const htmlDoc = this.getMHCPWordHTML();

    const plain = document.getElementById("print-root")?.innerText || "";
    try {
          if (navigator.clipboard && window.ClipboardItem) {
            await navigator.clipboard.write([
              new window.ClipboardItem({
                "text/html": new Blob([htmlDoc], { type: "text/html" }),
                "text/plain": new Blob([plain],   { type: "text/plain" }),
              }),
            ]);
            window.alert("Copied with table formatting. Paste into Word with Ctrl/Cmd+V.");
            return;
          }
          // Fallbacks
          if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(plain);
            window.alert("Copied as plain text (browser doesn’t allow HTML clipboard).");
            return;
          }
          const ta = document.createElement("textarea");
          ta.value = plain; document.body.appendChild(ta); ta.select();
          document.execCommand("copy"); document.body.removeChild(ta);
          window.alert("Copied as plain text.");
        } catch (e) {
          console.error(e);
          window.alert("Copy failed (clipboard permissions?).");
        }
      
  };

  // ---- Render pieces ----
  // renderPreview() {
  //   const s = this.state;
  //   const {
  //     mhcp = {},
  //     mhformulation = {},
  //     mhsafety = {},
  //     relapsePrevention = {},
  //   } = this.props.value || {};

  //   const renderMultiline = (val) => {
  //     if (!val) return null;
  //     if (Array.isArray(val)) {
  //       return val.map((line, idx) => <div key={idx}>{line}</div>);
  //     }
  //     return <div>{val}</div>;
  //   };

  //   // after: const ai = JSON.parse(payload?.respond);

  // const mse = typeof mhcp.mentalStateExamination === "object"
  //     ? Object.entries(mhcp.mentalStateExamination).map(
  //         ([k, v]) => `${k}: ${v}`
  //       )
  //     : mhcp?.mentalStateExamination
  //     ? [String(mhcp.mentalStateExamination)]  // wrap single string into array
  //     : [];

  //   const subscription = this.props.hasSubscription;
  //   if (subscription === null) {
  //     return null;
  //   }
  //   return (
  //     <div
  //       id="mhcp-print-root"
  //       className={`rounded-2xl border border-gray-200 bg-white p-6 shadow-sm print:border-0 print:shadow-none
  //         ${this.state.hasSubscription === false ? "select-none" : ""}`}
  //     >
  //    <button
  //       className={`
  //         bg-sky-700 text-white px-3 py-2 rounded-md text-md mb-3
  //         transition
  //         ${
  //           subscription === false
  //             ? "opacity-40 cursor-not-allowed"
  //             : "hover:bg-sky-800"
  //         }
  //       `}
  //       disabled={this.props.hasSubcription === false}
  //       onClick={
  //         subscription === false
  //           ? undefined
  //           : this.copyMHCPToClipboard
  //       }
  //     >
  //       Copy MHCP
  //     </button>

  //       <div className="prose max-w-none relative text-sm text-black">
  //         {/* Letterhead */}
  //         <div className="text-3xl font-bold text-center whitespace-pre-wrap">
  //           {s.practiceLetterhead}
  //         </div>

  //         {!subscription && (
  //           <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
  //             <div className="px-12 py-20 border-4 border-red-600 text-red-600 text-[10rem] font-extrabold uppercase tracking-[0.2em] opacity-40 rotate-[-20deg]">
  //               Demo Only
  //             </div>
  //           </div>
  //         )}

  //         {/* Title */}
  //         <h2 className="mt-2 text-xl font-bold text-center">
  //           GP MENTAL HEALTH TREATMENT PLAN – ADULT
  //         </h2>
  //         <p className="text-xs text-gray-600 text-center">
  //           MBS Items 2715
  //         </p>

  //         CONTACT & DEMOGRAPHIC DETAILS (reuse your patient header)
  //         <h3 className="mt-4 font-semibold">Contact and Demographic Details</h3>
  //         <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
  //           <div>
  //             <strong>GP name:</strong> {s.gpName || ""}
  //           </div>
  //           <div>
  //             <strong>GP phone:</strong> {s.gpPhone || ""}
  //           </div>
  //           <div>
  //             <strong>GP practice name:</strong> {s.practiceName || ""}
  //           </div>
  //           <div>
  //             <strong>GP address:</strong> {s.practiceAddress || ""}
  //           </div>
  //           <div>
  //             <strong>Provider number:</strong> {s.providerNumber || ""}
  //           </div>
  //         </div>

  //         <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
  //           <div className="md:col-span-2">
  //             <strong>Patient name:</strong> {s.ptFullName || ""}
  //           </div>
  //           <div>
  //             <strong>Date of birth:</strong> {formatDate(s.ptDoB)}
  //           </div>
  //           <div className="md:col-span-2">
  //             <strong>Patient address:</strong>{" "}
  //             {[s.ptStreet, s.ptCity, s.ptPostcode, s.ptState]
  //               .filter(Boolean)
  //               .join(" ")}
  //           </div>
  //           <div>
  //             <strong>Phone:</strong> {s.ptPhoneMob || s.ptPhoneH || ""}
  //           </div>
  //         </div>
  //         <hr className="border-t border-gray-300 my-4"></hr>
  //         {/* PATIENT ASSESSMENT – MENTAL HEALTH */}
  //         <h3 className="mt-6 font-semibold">Patient Assessment – Mental Health</h3>

  //         <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
  //           <div>
  //             <h4 className="font-semibold">Reasons for presenting</h4>
  //             <div className="whitespace-pre-wrap">
  //               {mhcp.presentingIssue}
  //             </div>
  //           </div>

  //           <div>
  //             <h4 className="font-semibold">History of current episode</h4>
  //             <div className="whitespace-pre-wrap">
  //               {mhcp.history}
  //             </div>
  //           </div>

  //           <div>
  //             <h4 className="font-semibold">Patient history</h4>
  //             <div className="whitespace-pre-wrap space-y-1">
  //               {mhcp.pastHistory && (
  //                 <div>
  //                   <strong>Mental / medical / social:</strong> {mhcp.pastHistory}
  //                 </div>
  //               )}
  //               {mhcp.occupation && (
  //                 <div>
  //                   <strong>Occupation:</strong> {mhcp.occupation}
  //                 </div>
  //               )}
  //               {mhcp.social && (
  //                 <div>
  //                   <strong>Salient social history:</strong> {mhcp.social}
  //                 </div>
  //               )}
  //               {mhcp.devTesting && (
  //                 <div>
  //                   <strong>Developmental issues / testing:</strong>{" "}
  //                   {mhcp.devTesting}
  //                 </div>
  //               )}
  //             </div>
  //           </div>

  //           <div>
  //             <h4 className="font-semibold">Family history of mental illness</h4>
  //             <div className="whitespace-pre-wrap">{mhcp.familyHistory}</div>
  //           </div>

  //           <div>
  //             <h4 className="font-semibold">Parent and children needs</h4>
  //             <div className="whitespace-pre-wrap">
  //               {mhcp.parentChildNeed}
  //             </div>
  //           </div>

  //           <div>
  //             <h4 className="font-semibold">Current domestic and social circumstances</h4>
  //             <div className="whitespace-pre-wrap">
  //               {mhcp.social}
  //             </div>
  //           </div>

  //           <div>
  //             <h4 className="font-semibold">Salient substance use issues</h4>
  //             <div className="whitespace-pre-wrap">
  //               {mhcp.substance}
  //             </div>
  //           </div>

  //           <div>
  //             <h4 className="font-semibold">Current medications</h4>
  //             <div className="whitespace-pre-wrap">
  //               {mhcp.currentMedication}
  //             </div>
  //           </div>

  //           <div>
  //             <h4 className="font-semibold">History of medication and other treatments</h4>
  //             <div className="whitespace-pre-wrap">
  //               {mhcp.treatmentHistory}
  //             </div>
  //           </div>

  //           <div>
  //             <h4 className="font-semibold">Allergies</h4>
  //             <div className="whitespace-pre-wrap">{mhcp.allergies}</div>
  //           </div>

  //           <div>
  //             <h4 className="font-semibold">
  //               Relevant physical examination and investigations
  //             </h4>
  //             <div className="whitespace-pre-wrap space-y-1">
  //               {mhcp.relevantExamination && (
  //                 <div>{mhcp.relevantExamination}</div>
  //               )}
  //               {mhcp.relevantInvestigation && (
  //                 <div>{mhcp.relevantInvestigation}</div>
  //               )}
  //             </div>
  //           </div>

  //           <div>
  //             <h4 className="font-semibold">
  //               Results of previous psychological / developmental testing
  //             </h4>
  //             <div className="whitespace-pre-wrap">{mhcp.devTesting}</div>
  //           </div>
  //         </div>
  //         <hr className="border-t border-gray-300 my-4"></hr>
  //         {/* MENTAL STATE EXAMINATION */}
  //         <h3 className="mt-6 font-semibold">
  //           Comments on Current Mental State Examination
  //         </h3>
  //         <p className="text-xs text-gray-600">
  //           Appearance, cognition, thought process, thought content, attention,
  //           memory, insight, behaviour, speech, mood and affect, perception,
  //           judgement, orientation.
  //         </p>
  //         <div className="mt-2 border border-gray-200 rounded-md p-3 text-sm space-y-1">
  //           {renderMultiline(mse) || (
  //             <div>No MSE comments recorded.</div>
  //           )}
  //         </div>

  //         {/* RISK ASSESSMENT */}
  //         <h3 className="mt-6 font-semibold">Risk Assessment</h3>
  //         <p className="text-xs text-gray-600">
  //           Suicide, self-harm, harm to others – ideation, intent, plan; document
  //           actions taken if high risk.
  //         </p>
  //         <div className="mt-2 border border-gray-200 rounded-md p-3 text-sm whitespace-pre-wrap">
  //           {mhcp.risk || "No specific risks documented."}
  //         </div>
  //         <hr className="border-t border-gray-300 my-4"></hr>
  //         {/* ASSESSMENT TOOL & DIAGNOSIS */}
  //         <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
  //           <div>
  //             <h4 className="font-semibold">Assessment / outcome tool used</h4>
  //             <div className="whitespace-pre-wrap">
  //               {mhcp.tools || ""}
  //             </div>
  //           </div>
  //           <div>
  //             <h4 className="font-bold">
  //               Provisional diagnosis of mental health disorder
  //             </h4>
  //             <div className="whitespace-pre-wrap">
  //               {mhcp.diagnosis || ""}
  //             </div>
  //           </div>
  //         </div>

  //         {/* CASE FORMULATION */}
  //         <h3 className="mt-6 font-bold ">Case Formulation</h3>
  //         <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
  //           <div>
  //             <strong>Predisposing factors</strong>
  //             <div className="whitespace-pre-wrap">
  //               {mhformulation.predisposing}
  //             </div>
  //           </div>
  //           <div>
  //             <strong>Precipitating factors</strong>
  //             <div className="whitespace-pre-wrap">
  //               {mhformulation.precipitating}
  //             </div>
  //           </div>
  //           <div>
  //             <strong>Perpetuating factors</strong>
  //             <div className="whitespace-pre-wrap">
  //               {mhformulation.perpetuating}
  //             </div>
  //           </div>
  //           <div>
  //             <strong>Protective factors</strong>
  //             <div className="whitespace-pre-wrap">
  //               {mhformulation.protective}
  //             </div>
  //           </div>
  //         </div>
  //         <hr className="border-t border-gray-300 my-4"></hr>
  //         {/* SAFETY PLAN / INTERVENTION & RELAPSE PREVENTION */}
  //         <h3 className="mt-6 font-bold">
  //           Intervention / Relapse Prevention Plan & Safety Plan
  //         </h3>
  //         <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
  //           <div>
  //             <h4 className="font-semibold">Reasons to live / stay safe</h4>
  //             <div className="whitespace-pre-wrap">
  //               {mhsafety.reason}
  //             </div>
  //           </div>

  //           <div>
  //             <h4 className="font-semibold">What I can do myself</h4>
  //             <div className="whitespace-pre-wrap">
  //               {mhsafety.cando}
  //             </div>
  //           </div>

  //           <div>
  //             <h4 className="font-semibold">Who can help me</h4>
  //             <div className="whitespace-pre-wrap">
  //               {mhsafety.help}
  //             </div>
  //           </div>

  //           <div>
  //             <h4 className="font-semibold">Who I can call</h4>
  //             <div className="whitespace-pre-wrap">
  //               {mhsafety.call}
  //             </div>
  //           </div>

  //           <div>
  //             <h4 className="font-semibold">What I can talk about</h4>
  //             <div className="whitespace-pre-wrap">
  //               {mhsafety.talk}
  //             </div>
  //           </div>

  //           <div>
  //             <h4 className="font-semibold">Professional / service contacts</h4>
  //             <div className="whitespace-pre-wrap">
  //               {mhsafety.professional}
  //             </div>
  //           </div>

  //           <div>
  //             <h4 className="font-semibold">Triggers</h4>
  //             <div className="whitespace-pre-wrap">
  //               {relapsePrevention.triggers}
  //             </div>
  //           </div>

  //           <div>
  //             <h4 className="font-semibold">Warning signs</h4>
  //             <div className="whitespace-pre-wrap">
  //               {relapsePrevention.warningSigns}
  //             </div>
  //           </div>

  //           <div>
  //             <h4 className="font-semibold">Coping strategies</h4>
  //             <div className="whitespace-pre-wrap">
  //               {relapsePrevention.copingStrategy}
  //             </div>
  //           </div>

  //           <div>
  //             <h4 className="font-semibold">Supports / services</h4>
  //             <div className="whitespace-pre-wrap">
  //               {relapsePrevention.support}
  //             </div>
  //           </div>

  //           <div className="md:col-span-2">
  //             <h4 className="font-semibold">Emergency / crisis arrangements</h4>
  //             <div className="whitespace-pre-wrap">
  //               {relapsePrevention.emergency}
  //             </div>
  //           </div>
  //         </div>

  //         {/* DATE & GP SIGNATURE */}
  //         <div className="mt-6 text-sm">
  //           <strong>Date plan completed:</strong> {formatDate(s.todaysDate)}
  //           <p></p>

  //           {s.gpSignatureDataUrl ? (
  //             <img
  //               src={s.gpSignatureDataUrl}
  //               alt="GP signature"
  //               className="h-16 object-contain"
  //               style={{ imageRendering: "auto" }}
  //             />
  //           ) : (
  //             <>
  //               <p></p>
  //               <span>_______________________________</span>
  //             </>
  //           )}

  //           <div>
  //             <strong>GP Name:</strong> {s.gpName || ""}
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  renderPreview() {
    const s = this.state;
    const {
      mhcp = {},
      mhformulation = {},
      mhsafety = {},
      relapsePrevention = {},
    } = this.props.value || {};

    const subscription = this.props.hasSubscription;
    if (subscription === null) return null;

    const renderCellContent = (val, fallback = "") => {
      if (
        !val ||
        (Array.isArray(val) && val.length === 0) ||
        (typeof val === "string" && !val.trim())
      ) {
        return <div className="whitespace-pre-wrap">{fallback}</div>;
      }

      if (Array.isArray(val)) {
        return val.map((item, idx) => (
          <div key={idx} className="whitespace-pre-wrap">
            {item}
          </div>
        ));
      }

      return <div className="whitespace-pre-wrap">{val}</div>;
    };

    const renderTableRow = (label, value, options = {}) => {
      const { fallback = "" } = options;

      return (
        <div className="grid grid-cols-[240px_1fr] border-b border-gray-200 last:border-b-0">
          <div className="bg-gray-50 px-3 py-2 font-semibold border-r border-gray-200">
            {label}
          </div>
          <div className="px-3 py-2 min-h-[44px]">
            {renderCellContent(value, fallback)}
          </div>
        </div>
      );
    };

    const renderFourColRow = (items) => {
      return (
        <div className="grid grid-cols-4 border-b border-gray-200 last:border-b-0">
          {items.map((item, i) => (
            <div
              key={i}
              className={`px-3 py-2 min-h-[44px] ${
                i % 2 === 0
                  ? "bg-gray-50 font-semibold border-r border-gray-200"
                  : "border-r border-gray-200"
              }`}
            >
              {item}
            </div>
          ))}
        </div>
      );
    };

    const renderSectionHeader = (title) => (
      <div className="mt-6 bg-sky-800 text-white font-bold px-3 py-2 rounded-t-md">
        {title}
      </div>
    );

    const patientAddress = [s.ptStreet, s.ptCity, s.ptPostcode, s.ptState]
      .filter(Boolean)
      .join(" ");

    const patientHistoryRows = [
      mhcp.pastHistory ? `Mental / medical / social: ${mhcp.pastHistory}` : null,
      mhcp.occupation ? `Occupation: ${mhcp.occupation}` : null,
      mhcp.social ? `Salient social history: ${mhcp.social}` : null,
      mhcp.devTesting
        ? `Developmental issues / testing: ${mhcp.devTesting}`
        : null,
    ].filter(Boolean);

    const relevantExamInvestigation = [
      mhcp.relevantExamination,
      mhcp.relevantInvestigation,
    ].filter(Boolean);

    const mseRows =
      typeof mhcp.mentalStateExamination === "object" &&
      mhcp.mentalStateExamination !== null &&
      !Array.isArray(mhcp.mentalStateExamination)
        ? Object.entries(mhcp.mentalStateExamination)
        : [];

    return (
      <div
        id="mhcp-print-root"
        className={`rounded-2xl border border-gray-200 bg-white p-6 shadow-sm print:border-0 print:shadow-none ${
          subscription === false ? "select-none" : ""
        }`}
      >
        <button
          className={`bg-sky-700 text-white px-3 py-2 rounded-md text-md mb-3 transition ${
            subscription === false
              ? "opacity-40 cursor-not-allowed"
              : "hover:bg-sky-800"
          }`}
          disabled={this.props.hasSubscription === false}
          onClick={subscription === false ? undefined : this.copyMHCPToClipboard}
        >
          Copy MHCP
        </button>

        <div className="max-w-none relative text-sm text-black">
          {/* Letterhead */}
          <div className="text-3xl font-bold text-center whitespace-pre-wrap">
            {s.practiceLetterhead}
          </div>

          {!subscription && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="px-12 py-20 border-4 border-red-600 text-red-600 text-[10rem] font-extrabold uppercase tracking-[0.2em] opacity-40 rotate-[-20deg]">
                Demo Only
              </div>
            </div>
          )}

          {/* Title */}
          <h2 className="mt-2 text-xl font-bold text-center">
            GP MENTAL HEALTH TREATMENT PLAN – ADULT
          </h2>
          <p className="text-xs text-gray-600 text-center">MBS Items 2715/2717</p>

          {/* Contact and demographic details */}
         {renderSectionHeader("Contact and Demographic Details")}
          <div className="border border-t-0 border-gray-200 rounded-b-md overflow-hidden text-sm">

            {renderFourColRow([
              "GP name",
              s.gpName || "",
              "GP phone",
              s.gpPhone || "",
            ])}

            {renderFourColRow([
              "Practice name",
              s.practiceName || "",
              "Provider number",
              s.providerNumber || "",
            ])}

            {renderFourColRow([
              "Practice address",
              s.practiceAddress || "",
              "",
              "",
            ])}

            {renderFourColRow([
              "Patient name",
              s.ptFullName || "",
              "Date of birth",
              formatDate(s.ptDoB),
            ])}

            {renderFourColRow([
              "Patient address",
              [s.ptStreet, s.ptCity, s.ptPostcode, s.ptState].filter(Boolean).join(" "),
              "Phone",
              s.ptPhoneMob || s.ptPhoneH || "",
            ])}

          </div>

          {/* Patient assessment */}
          {renderSectionHeader("Patient Assessment – Mental Health")}
          <div className="border border-t-0 border-gray-200 rounded-b-md overflow-hidden text-sm">
            {renderTableRow("Reasons for presenting", mhcp.presentingIssue)}
            {renderTableRow("History of current episode", mhcp.history)}
            {renderTableRow("Patient history", patientHistoryRows)}
            {renderTableRow(
              "Family history of mental illness",
              mhcp.familyHistory
            )}
            {renderTableRow("Parent and children needs", mhcp.parentChildNeed)}
            {renderTableRow(
              "Current domestic and social circumstances",
              mhcp.social
            )}
            {renderTableRow("Salient substance use issues", mhcp.substance)}
            {renderTableRow("Current medications", mhcp.currentMedication)}
            {renderTableRow(
              "History of medication and other treatments",
              mhcp.treatmentHistory
            )}
            {renderTableRow("Allergies", mhcp.allergies)}
            {renderTableRow(
              "Relevant physical examination and investigations",
              relevantExamInvestigation
            )}
            {renderTableRow(
              "Results of previous psychological / developmental testing",
              mhcp.devTesting
            )}
          </div>

          {/* MSE */}
          {renderSectionHeader("Comments on Current Mental State Examination")}
          <div className="border border-t-0 border-gray-200 rounded-b-md overflow-hidden text-sm">
            {mseRows.length > 0 ? (
              mseRows.map(([key, value]) => renderTableRow(key, value))
            ) : (
              renderTableRow("Comments", mhcp.mentalStateExamination, {
                fallback: "No MSE comments recorded.",
              })
            )}
          </div>
          <div className="text-xs text-gray-600 mt-1 px-1">
            Appearance, cognition, thought process, thought content, attention,
            memory, insight, behaviour, speech, mood and affect, perception,
            judgement, orientation.
          </div>

          {/* Risk assessment */}
          {renderSectionHeader("Risk Assessment")}
          <div className="border border-t-0 border-gray-200 rounded-b-md overflow-hidden text-sm">
            {renderTableRow("Risk details", mhcp.risk, {
              fallback: "No specific risks documented.",
            })}
          </div>
          <div className="text-xs text-gray-600 mt-1 px-1">
            Suicide, self-harm, harm to others – ideation, intent, plan; document
            actions taken if high risk.
          </div>

          {/* Assessment and diagnosis */}
          {renderSectionHeader("Assessment and Diagnosis")}
          <div className="border border-t-0 border-gray-200 rounded-b-md overflow-hidden text-sm">
            {renderTableRow("Assessment / outcome tool used", mhcp.tools)}
            {renderTableRow(
              "Provisional diagnosis of mental health disorder",
              mhcp.diagnosis
            )}
          </div>

          {/* Case formulation */}
          {renderSectionHeader("Case Formulation")}
          <div className="border border-t-0 border-gray-200 rounded-b-md overflow-hidden text-sm">
            {renderTableRow("Predisposing factors", mhformulation.predisposing)}
            {renderTableRow("Precipitating factors", mhformulation.precipitating)}
            {renderTableRow("Perpetuating factors", mhformulation.perpetuating)}
            {renderTableRow("Protective factors", mhformulation.protective)}
          </div>

          {/* Safety / relapse prevention */}
          {renderSectionHeader("Intervention / Relapse Prevention Plan & Safety Plan")}
          <div className="border border-t-0 border-gray-200 rounded-b-md overflow-hidden text-sm">

            {renderFourColRow([
              "Reasons to live / stay safe",
              mhsafety.reason || "",
              "What I can do myself",
              mhsafety.cando || "",
            ])}

            {renderFourColRow([
              "Who can help me",
              mhsafety.help || "",
              "Who I can call",
              mhsafety.call || "",
            ])}

            {renderFourColRow([
              "What I can talk about",
              mhsafety.talk || "",
              "Professional contacts",
              mhsafety.professional || "",
            ])}

            {renderFourColRow([
              "Triggers",
              relapsePrevention.triggers || "",
              "Warning signs",
              relapsePrevention.warningSigns || "",
            ])}

            {renderFourColRow([
              "Coping strategies",
              relapsePrevention.copingStrategy || "",
              "Supports / services",
              relapsePrevention.support || "",
            ])}

            {renderFourColRow([
              "Emergency / crisis arrangements",
              relapsePrevention.emergency || "",
              "",
              "",
            ])}

          </div>

          {/* Signature */}
          {renderSectionHeader("Plan Completion")}
          <div className="border border-t-0 border-gray-200 rounded-b-md overflow-hidden text-sm">
            {renderTableRow("Date plan completed", formatDate(s.todaysDate))}
            <div className="grid grid-cols-[240px_1fr] border-b border-gray-200 last:border-b-0">
              <div className="bg-gray-50 px-3 py-2 font-semibold border-r border-gray-200">
                GP signature
              </div>
              <div className="px-3 py-2 min-h-[44px]">
                {s.gpSignatureDataUrl ? (
                  <img
                    src={s.gpSignatureDataUrl}
                    alt="GP signature"
                    className="h-16 object-contain"
                    style={{ imageRendering: "auto" }}
                  />
                ) : (
                  <span></span>
                )}
              </div>
            </div>
            {renderTableRow("GP Name", s.gpName || "")}
          </div>
        </div>
      </div>
    );
  }


  renderSmartPasteModal() {
    if (!this.state.smartPasteOpen) return null;

    return (
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) this.closeSmartPaste();
        }}
      >
        <div
          className="bg-white rounded-2xl shadow-xl p-4 flex flex-col"
          style={{ width: 800, height: 800 }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="mb-2">
            <h3 className="text-lg font-semibold">Smart Paste</h3>
            <p className="text-xs text-gray-500">
              Paste unstructured notes here. On submit, we’ll send it to your AI endpoint.
            </p>
          </div>

          <textarea
            className="flex-1 w-full resize-none rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black/30"
            placeholder="Paste or type notes…"
            value={this.state.smartPasteText}
            onChange={(e) =>
              this.setState({ smartPasteText: e.target.value, smartPasteError: "" })
            }
          />

          {this.state.smartPasteError && (
            <div className="mt-2 text-sm text-red-600">
              {this.state.smartPasteError}
            </div>
          )}

          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              className="rounded-xl border px-3 py-2 text-sm hover:bg-gray-50"
              onClick={this.closeSmartPaste}
              disabled={this.state.smartPasteBusy}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`rounded-xl px-3 py-2 text-sm text-white ${
                this.state.smartPasteBusy
                  ? "bg-gray-500"
                  : "bg-black hover:bg-black/90"
              }`}
              onClick={() => {
                this.toastId = toast.loading("Processing...");
                this.handleTextProcessing(this.toastId);
              }}
              disabled={this.state.smartPasteBusy}
            >
              {this.state.smartPasteBusy ? "Submitting…" : "Submit"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // renderForm() {

  //   const {
  //     isEditingMHCP,
  //     isEditingFormulation,
  //     isEditingSafety,
  //     isEditingRelapse,
  //     isHelper,
  //   } = this.state;

  //   const {
  //     mhcp = {},
  //     mhformulation = {},
  //     mhsafety = {},
  //     relapsePrevention = {},
  //   } = this.props.value || {};

  //   const profileFields = [
  //     { label: 'Presenting Issue', key: 'presentingIssue' },
  //     { label: 'History', key: 'history' },
  //     { label: 'Past History', key: 'pastHistory' },
  //     { label: 'Childhood History', key: 'childhoodHistory' },
  //     { label: 'Family History', key: 'familyHistory' },
  //     { label: 'Parent and Children Needs', key: 'parentChildNeed' },
  //     { label: 'Employment History', key: 'occupation' },
  //     { label: 'Social History', key: 'social' },
  //     { label: 'Substance Use History', key: 'substance' },
  //     { label: 'Current Medication', key: 'currentMedication' },
  //     { label: 'Allergies', key: 'allergies' },
  //     { label: 'Relevant Physical Examination', key: 'relevantExamination' },
  //     { label: 'Relevant Investigations', key: 'relevantInvestigation' },
  //     { label: 'Previous psychological and Development Testing', key: 'devTesting' },
  //     { label: 'Treatment History', key: 'treatmentHistory' },
  //     { label: 'Medication History', key: 'medicationHistory' },
  //     { label: 'Mental State Examination', key: 'mentalStateExamination' },
  //     { label: 'Risk and Comorbidities', key: 'risk' },
  //     { label: 'Diagnosis', key: 'diagnosis' },
  //     { label: 'Date', key: 'date' },
  //   ];

  //   const formulationFields = [
  //     { label: 'Predisposing factor', key: 'predisposing' },
  //     { label: 'Precipitating factor', key: 'precipitating' },
  //     { label: 'Perpetuating factor', key: 'perpetuating' },
  //     { label: 'Protective factor', key: 'protective' },
  //     { label: 'Date', key: 'date' },
  //   ];

  //   const safetyFields = [
  //     { label: 'Reason to Live', key: 'reason' },
  //     { label: 'What I can do to keep me safe', key: 'cando' },
  //     { label: 'Where can I seek help', key: 'help' },
  //     { label: 'Who can I call', key: 'call' },
  //     { label: 'Who can I talk to', key: 'talk' },
  //     { label: 'What professional help can I reach', key: 'professional' },
  //     { label: 'Date', key: 'date' },
  //   ];

  //   const preventionFields = [
  //     { label: 'Possible Triggers', key: 'triggers' },
  //     { label: 'Warning Signs', key: 'warningSigns' },
  //     { label: 'Coping Strategies', key: 'copingStrategy' },
  //     { label: 'Support System', key: 'support' },
  //     { label: 'Emergency Plan', key: 'emergency' },
  //     { label: 'Date', key: 'date' },
  //   ];

  //   return (
  //     <div className="grid grid-cols-6 xs:grid-cols-2 sm:grid-cols-4 relative">
  //       {/* Helper checklist */}
  //       {isHelper && (
  //         <div
  //           id="checklist"
  //           className="fixed -mt-60 right-4 w-[400px] h-auto bg-yellow-100 shadow rounded-md p-4"
  //           onClick={() => this.setState({ isHelper: false })}
  //         >
  //           <p className="text-lg font-semibold text-red-700 text-center">No Entry yet</p>
  //           {profileFields.map((field) =>
  //             mhcp[field.key] === "" ? (
  //               <div key={field.key} className="p-1">- {field.label}</div>
  //             ) : null
  //           )}
  //           {formulationFields.map((field) =>
  //             mhformulation[field.key] === "" ? (
  //               <div key={field.key} className="p-1">- {field.label}</div>
  //             ) : null
  //           )}
  //           {safetyFields.map((field) =>
  //             mhsafety[field.key] === "" ? (
  //               <div key={field.key} className="p-1">- {field.label}</div>
  //             ) : null
  //           )}
  //           {preventionFields.map((field) =>
  //             relapsePrevention[field.key] === "" ? (
  //               <div key={field.key} className="p-1">- {field.label}</div>
  //             ) : null
  //           )}
  //         </div>
  //       )}

  //       {/* MHCP / History */}
  //       <div className="col-span-4 row-span-3 p-4 bg-slate-100 rounded shadow-md m-2 xs:col-span-2">
  //         <div className="flex justify-between items-center">
  //           <h2 className="text-xl font-bold text-center">History</h2>
  //           <button
  //             onClick={() => this.toggleEdit('isEditingMHCP')}
  //             className={`px-4 py-2 rounded text-white ${
  //               isEditingMHCP
  //                 ? 'bg-sky-600 hover:bg-sky-700'
  //                 : 'bg-yellow-500 hover:bg-yellow-600'
  //             }`}
  //           >
  //             {isEditingMHCP ? 'Save' : 'Edit'}
  //           </button>
  //         </div>
  //         <div className="mt-4 grid grid-cols-2 gap-4 xs:grid-cols-1">
  //           {profileFields.map((field) => (
  //             <div key={field.key}>
  //               <h3 className="text-sm font-semibold">{field.label}</h3>
  //               {field.key === 'date' ? (
  //                 <input
  //                   type="date"
  //                   className="w-full text-black text-sm border-gray-200"
  //                   value={mhcp.date || ""}
  //                   readOnly={!isEditingMHCP}
  //                   onChange={(e) =>
  //                     this.handleSectionChange('mhcp', 'date', e.target.value)
  //                   }
  //                   disabled={!isEditingMHCP}
  //                 />
  //               ) : (
  //                 <textarea
  //                   className="w-full h-40 resize-none text-black text-sm border-gray-200"
  //                   value={mhcp[field.key] || ""}
  //                   readOnly={!isEditingMHCP}
  //                   onChange={(e) =>
  //                     this.handleSectionChange('mhcp', field.key, e.target.value)
  //                   }
  //                   disabled={!isEditingMHCP}
  //                 />
  //               )}
  //             </div>
  //           ))}
  //         </div>
  //       </div>

  //       {/* Formulation */}
  //       <div className="col-span-2 p-4 bg-slate-100 rounded shadow-md m-2">
  //         <div className="flex justify-between items-center">
  //           <h2 className="text-lg font-bold text-center">Formulation</h2>
  //           <button
  //             onClick={() => this.toggleEdit('isEditingFormulation')}
  //             className={`px-4 py-2 rounded text-white ${
  //               isEditingFormulation
  //                 ? 'bg-sky-600 hover:bg-sky-700'
  //                 : 'bg-yellow-500 hover:bg-yellow-600'
  //             }`}
  //           >
  //             {isEditingFormulation ? 'Save' : 'Edit'}
  //           </button>
  //         </div>
  //         <div className="mt-4 grid grid-cols-2 gap-4 xs:grid-cols-1">
  //           {formulationFields.map((field) => (
  //             <div key={field.key}>
  //               <h3 className="text-sm font-semibold">{field.label}</h3>
  //               {field.key === 'date' ? (
  //                 <input
  //                   type="date"
  //                   className="w-full text-black text-sm border-gray-200"
  //                   value={mhformulation.date || ""}
  //                   readOnly={!isEditingFormulation}
  //                   onChange={(e) =>
  //                     this.handleSectionChange('mhformulation', 'date', e.target.value)
  //                   }
  //                   disabled={!isEditingFormulation}
  //                 />
  //               ) : (
  //                 <textarea
  //                   className="w-full h-40 resize-none text-black text-sm border-gray-200"
  //                   value={mhformulation[field.key] || ""}
  //                   readOnly={!isEditingFormulation}
  //                   onChange={(e) =>
  //                     this.handleSectionChange('mhformulation', field.key, e.target.value)
  //                   }
  //                   disabled={!isEditingFormulation}
  //                 />
  //               )}
  //             </div>
  //           ))}
  //         </div>
  //       </div>

  //       {/* Safety Plan */}
  //       <div className="col-span-2 p-4 bg-slate-100 rounded shadow-md m-2">
  //         <div className="flex justify-between items-center">
  //           <h2 className="text-lg font-bold text-center">Safety Plan</h2>
  //           <button
  //             onClick={() => this.toggleEdit('isEditingSafety')}
  //             className={`px-4 py-2 rounded text-white ${
  //               isEditingSafety
  //                 ? 'bg-sky-600 hover:bg-sky-700'
  //                 : 'bg-yellow-500 hover:bg-yellow-600'
  //             }`}
  //           >
  //             {isEditingSafety ? 'Save' : 'Edit'}
  //           </button>
  //         </div>
  //         <div className="mt-4 grid grid-cols-2 gap-4 xs:grid-cols-1">
  //           {safetyFields.map((field) => (
  //             <div key={field.key}>
  //               <h3 className="text-sm font-semibold">{field.label}</h3>
  //               {field.key === 'date' ? (
  //                 <input
  //                   type="date"
  //                   className="w-full text-black text-sm border-gray-200"
  //                   value={mhsafety.date || ""}
  //                   readOnly={!isEditingSafety}
  //                   onChange={(e) =>
  //                     this.handleSectionChange('mhsafety', 'date', e.target.value)
  //                   }
  //                   disabled={!isEditingSafety}
  //                 />
  //               ) : (
  //                 <textarea
  //                   className="w-full h-40 resize-none text-black text-sm border-gray-200"
  //                   value={mhsafety[field.key] || ""}
  //                   readOnly={!isEditingSafety}
  //                   onChange={(e) =>
  //                     this.handleSectionChange('mhsafety', field.key, e.target.value)
  //                   }
  //                   disabled={!isEditingSafety}
  //                 />
  //               )}
  //             </div>
  //           ))}
  //         </div>
  //       </div>

  //       {/* Relapse Prevention */}
  //       <div className="col-span-2 p-4 bg-slate-100 rounded shadow-md m-2">
  //         <div className="flex justify-between items-center">
  //           <h2 className="text-lg font-bold text-center">Relapse Prevention Plan</h2>
  //           <button
  //             onClick={() => this.toggleEdit('isEditingRelapse')}
  //             className={`px-4 py-2 rounded text-white ${
  //               isEditingRelapse
  //                 ? 'bg-sky-600 hover:bg-sky-700'
  //                 : 'bg-yellow-500 hover:bg-yellow-600'
  //             }`}
  //           >
  //             {isEditingRelapse ? 'Save' : 'Edit'}
  //           </button>
  //         </div>
  //         <div className="mt-4 grid grid-cols-2 gap-4 xs:grid-cols-1">
  //           {preventionFields.map((field) => (
  //             <div key={field.key}>
  //               <h3 className="text-sm font-semibold">{field.label}</h3>
  //               {field.key === 'date' ? (
  //                 <input
  //                   type="date"
  //                   className="w-full text-black text-sm border-gray-200"
  //                   value={relapsePrevention.date || ""}
  //                   readOnly={!isEditingRelapse}
  //                   onChange={(e) =>
  //                     this.handleSectionChange('relapsePrevention', 'date', e.target.value)
  //                   }
  //                   disabled={!isEditingRelapse}
  //                 />
  //               ) : (
  //                 <textarea
  //                   className="w-full h-40 resize-none text-black text-sm border-gray-200"
  //                   value={relapsePrevention[field.key] || ""}
  //                   readOnly={!isEditingRelapse}
  //                   onChange={(e) =>
  //                     this.handleSectionChange('relapsePrevention', field.key, e.target.value)
  //                   }
  //                   disabled={!isEditingRelapse}
  //                 />
  //               )}
  //             </div>
  //           ))}
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  renderForm() {
  const {
    isEditingMHCP,
    isEditingFormulation,
    isEditingSafety,
    isEditingRelapse,
    isHelper,
  } = this.state;

  const {
    mhcp = {},
    mhformulation = {},
    mhsafety = {},
    relapsePrevention = {},
  } = this.props.value || {};

  const profileFields = [
    { label: "Presenting Issue", key: "presentingIssue" },
    { label: "History", key: "history" },
    { label: "Past History", key: "pastHistory" },
    { label: "Childhood History", key: "childhoodHistory" },
    { label: "Family History", key: "familyHistory" },
    { label: "Parent and Children Needs", key: "parentChildNeed" },
    { label: "Employment History", key: "occupation" },
    { label: "Social History", key: "social" },
    { label: "Substance Use History", key: "substance" },
    { label: "Current Medication", key: "currentMedication" },
    { label: "Allergies", key: "allergies" },
    { label: "Relevant Physical Examination", key: "relevantExamination" },
    { label: "Relevant Investigations", key: "relevantInvestigation" },
    {
      label: "Previous psychological and Development Testing",
      key: "devTesting",
    },
    { label: "Treatment History", key: "treatmentHistory" },
    { label: "Medication History", key: "medicationHistory" },
    { label: "Mental State Examination", key: "mentalStateExamination" },
    { label: "Risk and Comorbidities", key: "risk" },
    { label: "Diagnosis", key: "diagnosis" },
    { label: "Date", key: "date" },
  ];

  const formulationFields = [
    { label: "Predisposing factor", key: "predisposing" },
    { label: "Precipitating factor", key: "precipitating" },
    { label: "Perpetuating factor", key: "perpetuating" },
    { label: "Protective factor", key: "protective" },
    { label: "Date", key: "date" },
  ];

  const safetyFields = [
    { label: "Reason to Live", key: "reason" },
    { label: "What I can do to keep me safe", key: "cando" },
    { label: "Where can I seek help", key: "help" },
    { label: "Who can I call", key: "call" },
    { label: "Who can I talk to", key: "talk" },
    { label: "What professional help can I reach", key: "professional" },
    { label: "Date", key: "date" },
  ];

  const preventionFields = [
    { label: "Possible Triggers", key: "triggers" },
    { label: "Warning Signs", key: "warningSigns" },
    { label: "Coping Strategies", key: "copingStrategy" },
    { label: "Support System", key: "support" },
    { label: "Emergency Plan", key: "emergency" },
    { label: "Date", key: "date" },
  ];

  const getMissingFields = (fields, data) =>
    fields.filter((field) => !String(data[field.key] || "").trim());

  const renderField = (
    sectionKey,
    field,
    value,
    isEditing,
    index,
    areaHeight = "h-32"
  ) => {
    const isDate = field.key === "date";

    return (
      <div
        key={field.key}
        className="group rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4 shadow-sm transition hover:shadow-md"
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">
              {field.label}
            </h3>
            <div className="mt-1 h-1 w-10 rounded-full bg-sky-500/70" />
          </div>
          <div className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-500">
            {String(index + 1).padStart(2, "0")}
          </div>
        </div>

        {isDate ? (
          <input
            type="date"
            className={`w-full rounded-xl border px-3 py-2 text-sm text-black shadow-sm outline-none transition ${
              isEditing
                ? "border-sky-200 bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-200"
                : "border-slate-200 bg-slate-100 text-slate-600 cursor-not-allowed"
            }`}
            value={value || ""}
            readOnly={!isEditing}
            disabled={!isEditing}
            onChange={(e) =>
              this.handleSectionChange(sectionKey, field.key, e.target.value)
            }
          />
        ) : (
          <textarea
            className={`w-full resize-none rounded-xl border px-3 py-3 text-sm text-black outline-none transition ${
              isEditing
                ? `${areaHeight} border-slate-200 bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-200`
                : `${areaHeight} border-slate-200 bg-slate-100 text-slate-700`
            }`}
            placeholder={isEditing ? `Enter ${field.label.toLowerCase()}...` : ""}
            value={value || ""}
            readOnly={!isEditing}
            disabled={!isEditing}
            onChange={(e) =>
              this.handleSectionChange(sectionKey, field.key, e.target.value)
            }
          />
        )}
      </div>
    );
  };

  const renderSection = ({
    title,
    subtitle,
    editKey,
    isEditing,
    sectionKey,
    fields,
    data,
    className = "",
    areaHeight = "h-32",
    headerClassName = "from-sky-900 via-sky-800 to-cyan-700",
  }) => (
    <div
      className={`overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-lg ${className}`}
    >
      <div
        className={`bg-gradient-to-r ${headerClassName} px-5 py-4 text-white`}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-bold tracking-tight">{title}</h2>
            <p className="mt-1 text-sm text-white/85">{subtitle}</p>
          </div>

          <button
            onClick={() => this.toggleEdit(editKey)}
            className={`inline-flex items-center justify-center rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow transition ${
              isEditing
                ? "bg-emerald-500 hover:bg-emerald-600"
                : "bg-amber-500 hover:bg-amber-600"
            }`}
          >
            {isEditing ? "Save Section" : "Edit Section"}
          </button>
        </div>
      </div>

      <div className="p-5 sm:p-6">
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {fields.map((field, index) =>
            renderField(
              sectionKey,
              field,
              data[field.key],
              isEditing,
              index,
              areaHeight
            )
          )}
        </div>
      </div>
    </div>
  );

  const missingProfile = getMissingFields(profileFields, mhcp);
  const missingFormulation = getMissingFields(formulationFields, mhformulation);
  const missingSafety = getMissingFields(safetyFields, mhsafety);
  const missingPrevention = getMissingFields(
    preventionFields,
    relapsePrevention
  );

  return (
    <div className="relative grid grid-cols-6 gap-2 xs:grid-cols-2 sm:grid-cols-4">
      {/* Helper checklist */}
      {isHelper && (
        <div
          id="checklist"
          className="fixed right-4 top-24 z-50 w-[420px] max-w-[95vw] rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-100 p-5 shadow-2xl"
        >
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-amber-900">
                Incomplete Entries
              </h3>
              <p className="text-sm text-amber-800">
                Click anywhere on this panel to close.
              </p>
            </div>
            <button
              type="button"
              onClick={() => this.setState({ isHelper: false })}
              className="rounded-xl bg-amber-500 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-600"
            >
              Close
            </button>
          </div>

          <div
            className="max-h-[70vh] overflow-auto space-y-4 pr-1"
            onClick={() => this.setState({ isHelper: false })}
          >
            {[
              {
                title: "History",
                items: missingProfile,
              },
              {
                title: "Formulation",
                items: missingFormulation,
              },
              {
                title: "Safety Plan",
                items: missingSafety,
              },
              {
                title: "Relapse Prevention",
                items: missingPrevention,
              },
            ].map((section) => (
              <div
                key={section.title}
                className="rounded-2xl border border-amber-200 bg-white/80 p-4"
              >
                <div className="mb-2 text-sm font-bold text-amber-900">
                  {section.title}
                </div>

                {section.items.length > 0 ? (
                  <div className="space-y-1">
                    {section.items.map((field) => (
                      <div
                        key={field.key}
                        className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-slate-700"
                      >
                        - {field.label}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-emerald-700 font-medium">
                    Complete
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* History */}
      <div className="col-span-4 row-span-3 m-2 xs:col-span-2">
        {renderSection({
          title: "History",
          subtitle:
            "Presenting concerns, psychosocial background, treatment history and clinical assessment.",
          editKey: "isEditingMHCP",
          isEditing: isEditingMHCP,
          sectionKey: "mhcp",
          fields: profileFields,
          data: mhcp,
          areaHeight: "h-36",
          className: "h-full",
          headerClassName: "from-sky-900 via-sky-800 to-cyan-700",
        })}
      </div>

      {/* Formulation */}
      <div className="col-span-2 m-2">
        {renderSection({
          title: "Formulation",
          subtitle:
            "Map predisposing, precipitating, perpetuating and protective factors.",
          editKey: "isEditingFormulation",
          isEditing: isEditingFormulation,
          sectionKey: "mhformulation",
          fields: formulationFields,
          data: mhformulation,
          areaHeight: "h-36",
          headerClassName: "from-violet-900 via-violet-800 to-fuchsia-700",
        })}
      </div>

      {/* Safety Plan */}
      <div className="col-span-2 m-2">
        {renderSection({
          title: "Safety Plan",
          subtitle:
            "Protective supports, contacts and immediate strategies to maintain safety.",
          editKey: "isEditingSafety",
          isEditing: isEditingSafety,
          sectionKey: "mhsafety",
          fields: safetyFields,
          data: mhsafety,
          areaHeight: "h-36",
          headerClassName: "from-emerald-900 via-emerald-800 to-teal-700",
        })}
      </div>

      {/* Relapse Prevention */}
      <div className="col-span-2 m-2">
        {renderSection({
          title: "Relapse Prevention Plan",
          subtitle:
            "Identify triggers, warning signs, coping strategies and emergency responses.",
          editKey: "isEditingRelapse",
          isEditing: isEditingRelapse,
          sectionKey: "relapsePrevention",
          fields: preventionFields,
          data: relapsePrevention,
          areaHeight: "h-36",
          headerClassName: "from-amber-900 via-orange-800 to-rose-700",
        })}
      </div>
    </div>
  );
}

  renderTools() {
    const currentCredits = this.props.currentCredits;
    const isDisabled = currentCredits < 10;

    return (
      <div className="mb-4 flex items-center justify-center space-x-4 pl-80 sm:-pl-[300px]">
        <div
          className={`
            pl-4 flex gap-4 items-center justify-center relative
            ${isDisabled ? "opacity-40 pointer-events-none" : ""}
          `}
          aria-disabled={isDisabled}
          title={
            isDisabled
              ? "You need at least 10 credits to use AI tools"
              : ""
          }
        >
          <IconButton
            label="AI Write or Paste"
            onClick={this.openSmartPaste}
          >
            <ClipboardDocumentListIcon className="w-8 h-8" />
          </IconButton>
          <div className='sm:-ml-[700px]'>
          <Transcribe onInputUpdate={this.handleTranscription} />
          </div>
          <div className="absolute left-14 -bottom-2 text-xs text-gray-500">
            AI Tools
          </div>
        </div>
      </div>
    );
  }



  renderActiveTabContent = () => {
    const { activeTab } = this.state;
    if (activeTab === "Form") return this.renderForm();
    return this.renderPreview();
  };

  renderTabs = () => {
    const { activeTab } = this.state;
    return (
      <div className="flex border-b mb-4">
        <button
          type="button"
          onClick={() => this.handleTabChange("Form")}
          className={
            "px-4 py-2 text-sm font-medium border-b-2 " +
            (activeTab === "Form"
              ? "border-sky-600 text-sky-700"
              : "border-transparent text-gray-500 hover:text-gray-700")
          }
        >
          Form
        </button>
        <button
          type="button"
          onClick={() => this.handleTabChange("Preview")}
          className={
            "px-4 py-2 text-sm font-medium border-b-2 " +
            (activeTab === "Preview"
              ? "border-sky-600 text-sky-700"
              : "border-transparent text-gray-500 hover:text-gray-700")
          }
        >
          Preview
        </button>
      </div>
    );
  };


  render() {
    return (
      <div
        className="col-span-6 grid-rows-4 xs:grid-cols-1 md:grid-cols-1 lg:grid-cols-3"
        style={{
          gridTemplateRows:
            'minmax(12px, auto) minmax(12px, auto) minmax(320px, auto)',
        }}
      >
        <div
          id="main"
          className="text-center col-span-6 text-3xl font-bold py-4 text-sky-900"
        >
          Mental Health Care Plan
        </div>

        {this.renderTools()}

        <div className="tabs col-span-6 my-4 flex justify-center">
          {/* tabs can be re-added here if needed */}
        </div>

        {/* <div className="tabContent col-span-6">
          {this.renderBackground()}
        </div> */}

        {this.renderTabs()}
        {this.renderActiveTabContent()}
        {this.renderSmartPasteModal()}


        <ToastContainer />
      </div>
    );
  }
}

export default MentalHealth;
